/**
 * DataRow — canonical row for verified factual data.
 *
 * Three columns: label · value · source. Used everywhere we render a
 * measured fact (parcel area, voltage, distance, etc.) so the visual
 * language stays consistent and every value cites where it came from.
 *
 * Audit 2026-05-27 (restructure): introduced to separate verified
 * facts (this) from speculative estimates (EstimateRow).
 *
 * `status` is optional — pass when the value itself is a signal
 * worth flagging (e.g. slope > 35% as a "concern"). Renders a dot
 * before the value.
 *
 * 2026-10-05: `onLocate` makes the row clickable (⌖) — the map draws a
 * dashed line from the site to the named feature and zooms to fit both.
 * 2026-10-09: ProvenanceIcon (ⓘ) when `field` has registered metadata —
 * source attribution with license, data date and links. The icon stops
 * click propagation, so both can sit on one row.
 * 2026-10-12: `sourceHref` turns the source into a link to the record
 * itself (utils/sourceLinks): the WattApp station, the property in Se
 * eiendom, the place on Norgeskart.
 */

import React from 'react';
import { ProvenanceIcon } from './ProvenancePopover';
import { FIELD_PROVENANCE } from '../utils/fieldProvenance';

const STATUS_CLASS = {
  verified: 'status-strong',   // green
  flagged:  'status-concern',  // amber
  blocker:  'status-blocker',  // red
  missing:  'status-unknown',  // gray
};

export default function DataRow({ label, value, source, sourceHref = null, status, mono = true, title, field, onLocate = null, country = 'NO', dataDate = null }) {
  // Skip the row entirely when value is null/undefined/empty — keeps
  // the table dense without "—" filler rows.
  if (value == null || value === '' || value === '—') return null;

  const clickable = typeof onLocate === 'function';
  // Show provenance icon if field has metadata registered
  const hasMeta = field && FIELD_PROVENANCE[field];

  return (
    <div
      className={`data-row${clickable ? ' data-row-locatable' : ''}`}
      title={clickable ? (title || 'Show on map') : (title || '')}
      data-field={field || undefined}
      onClick={clickable ? onLocate : undefined}
      role={clickable ? 'button' : undefined}
      style={clickable ? { cursor: 'pointer' } : undefined}
    >
      <span className="label">
        {clickable && <span style={{ color: '#60a5fa', marginRight: 5 }} aria-hidden="true">⌖</span>}
        {label}
        {hasMeta && <ProvenanceIcon field={field} label={label} country={country} dataDate={dataDate} />}
      </span>
      <span className={mono ? 'value numeric' : 'value'} style={mono ? undefined : { fontFamily: 'inherit' }}>
        {status && <span className={`status-dot ${STATUS_CLASS[status] || ''}`} />}
        {value}
      </span>
      {source && (sourceHref
        ? <a className="source source-link" href={sourceHref} target="_blank" rel="noopener noreferrer"
             title="Open the record at the source" onClick={e => e.stopPropagation()}>{source} ↗</a>
        : <span className="source">{source}</span>)}
    </div>
  );
}
